import React, { useState, useEffect } from "react";
import "../AddAdvertisement.css";
import { isSpecialUser } from "../../../../../utils/environment";
import WeekdayOnlyDatePicker from "../components/WeekdayOnlyDatePicker";

// MissionAction.ma_id (서버 script/seed_sns_promotion_mission.js 로 등록한 "인스타 홍보")
export const INSTA_PROMOTION_MA_ID = 14;
// MissionService.name (SNS 홍보 서비스)
export const SNS_PROMOTION_SERVICE_NAME = "SNS 홍보";

/** SNS 홍보(인스타 홍보)는 스페셜 아이디(REACT_APP_SPECIAL_USER_IDS)만 노출 */
export const canUseSnsPromotion = (userId) => isSpecialUser(userId);

/** 서비스 탭 목록에서 권한 없는 SNS 홍보 제외 ({ originalName } 형태) */
export const filterServicesForUser = (services, userId) =>
  canUseSnsPromotion(userId)
    ? services
    : services.filter((s) => s.originalName !== SNS_PROMOTION_SERVICE_NAME);

/** 액션 탭 목록에서 권한 없는 인스타 홍보 제외 ({ ma_id } 형태) */
export const filterActionsForUser = (actions, userId) =>
  canUseSnsPromotion(userId)
    ? actions
    : actions.filter((a) => a.ma_id !== INSTA_PROMOTION_MA_ID);

/** "금뚝딱, #협찬" → ["금뚝딱", "#협찬"] (서버 InstaMissionJudge.parseRequiredWords 와 같은 규칙) */
export const parseRequiredWords = (value) =>
  String(value ?? "")
    .split(",")
    .map((w) => w.trim())
    .filter(Boolean);

/** memo(JSON 문자열)에서 셀러가 지정한 필수 URL (선택, 없으면 "") */
export const requiredUrlFromMemo = (memo) => {
  try {
    return (memo ? JSON.parse(memo) : {}).requiredUrl || "";
  } catch {
    return "";
  }
};

/** 필수 URL 형식 검사 (비어 있으면 통과) */
export const isValidRequiredUrl = (value) => {
  const v = String(value ?? "").trim();
  return !v || /^(https?:\/\/)?[^\s/]+\.[^\s]+$/i.test(v);
};

/** memo(JSON 문자열)에서 게시 가이드 문구 읽기 */
export const guideTextFromMemo = (memo) => {
  try {
    return (memo ? JSON.parse(memo) : {}).guideText || "";
  } catch {
    return "";
  }
};

const InstaPromotionPage = ({
  formData,
  onInputChange,
  user,
  onNext,
  minStartDate,
  step = 1,
  zoomSettings,
}) => {
  const [currentStep, setCurrentStep] = useState(step);

  useEffect(() => {
    setCurrentStep(step);
  }, [step]);

  // 광고 기간 자동 계산
  useEffect(() => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);
      if (endDate >= startDate) {
        const daysDiff =
          Math.ceil(
            (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24),
          ) + 1;
        onInputChange("adDuration", daysDiff.toString());
      }
    }
  }, [formData.adStartDate, formData.adEndDate]);

  // 결제 금액 = 신청 수량 × 1회 단가
  useEffect(() => {
    const totalQuantity = parseInt(formData.totalQuantity) || 0;
    const userPriceByOne = parseFloat(formData.userPriceByOne) || 0;
    onInputChange("price", (totalQuantity * userPriceByOne).toString());
  }, [formData.totalQuantity, formData.userPriceByOne]);

  const getTomorrowDate = () => {
    const koreaTime = new Date(Date.now() + 9 * 60 * 60 * 1000);
    koreaTime.setDate(koreaTime.getDate() + 1);
    return koreaTime.toISOString().split("T")[0];
  };

  const minDate = minStartDate || getTomorrowDate();
  const userIsSpecial = isSpecialUser(user?.id);
  const effectiveMinDate = userIsSpecial ? "" : minDate;

  const validateDate = (dateString) => {
    if (!dateString) return "날짜를 선택해주세요.";
    const selectedDate = new Date(dateString);
    if (isNaN(selectedDate.getTime())) return "올바른 날짜 형식이 아닙니다.";
    if (!userIsSpecial && selectedDate < new Date(minDate)) {
      return "광고 시작일은 주문 마감 기준 선택 가능한 날짜 이후여야 합니다.";
    }
    return "";
  };

  const handleAdStartDateChange = (value) => {
    if (value) {
      const error = validateDate(value);
      if (error) {
        onInputChange("adStartDate", "");
        alert(error);
        return;
      }
      // 휴무로 설정된 토/일요일은 선택 불가 (VVIP 또는 특정 유저 예외)
      const dayOfWeek = new Date(value).getDay();
      const allowWeekendByGrade = user?.price_grade?.name === "VVIP";
      const isSelectedDayOff =
        (dayOfWeek === 6 && !!zoomSettings?.end_submit_sat_off) ||
        (dayOfWeek === 0 && !!zoomSettings?.end_submit_sun_off);
      if (isSelectedDayOff && !allowWeekendByGrade && !userIsSpecial) {
        onInputChange("adStartDate", "");
        alert(
          "이 날은 주문을 받지 않는 휴무일입니다. 다른 날짜를 선택해주세요.",
        );
        return;
      }
    }

    onInputChange("adStartDate", value);

    if (value) {
      const duration = parseInt(formData.adDuration) || 0;
      if (duration > 0) {
        const endDate = new Date(value);
        endDate.setDate(endDate.getDate() + duration - 1);
        onInputChange("adEndDate", endDate.toISOString().split("T")[0]);
      }
    } else {
      onInputChange("adEndDate", "");
      onInputChange("adDuration", "");
    }
  };

  const handleAdEndDateChange = (value) => {
    if (value) {
      const error = validateDate(value);
      if (error) {
        onInputChange("adEndDate", "");
        alert(error);
        return;
      }
    }
    onInputChange("adEndDate", value);
  };

  const handleAdDurationChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (!formData.adStartDate) {
      alert("광고 시작일을 먼저 선택해주세요.");
      return;
    }
    if (value === "") {
      onInputChange("adDuration", "");
      onInputChange("adEndDate", "");
      return;
    }
    const duration = parseInt(value) || 0;
    if (duration <= 0) {
      alert("진행 일수는 1일 이상이어야 합니다.");
      return;
    }
    const endDate = new Date(formData.adStartDate);
    endDate.setDate(endDate.getDate() + duration - 1);
    onInputChange("adDuration", duration.toString());
    onInputChange("adEndDate", endDate.toISOString().split("T")[0]);
  };

  const requiredWords = parseRequiredWords(formData.approveAnswer);
  const hasDisclosureTag = requiredWords.some((w) =>
    ["#광고", "#협찬", "#유료광고"].includes(w),
  );

  // 주문 확인 화면
  if (currentStep === 2) {
    return (
      <div className="shopping-visit-confirmation">
        <div className="spacer"></div>
        <div className="product-info-section">
          <div className="product-info-grid">
            <div className="info-row">
              <div className="form-field">
                <label>홍보 대상</label>
                <input type="text" value={formData.storeName || ""} readOnly />
              </div>
              <div className="form-field">
                <label>필수 단어</label>
                <input type="text" value={requiredWords.join(", ")} readOnly />
              </div>
            </div>
            <div className="info-row">
              <div className="form-field">
                <label>게시 가이드</label>
                <input
                  type="text"
                  value={formData.instaGuideText || "-"}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>포함할 URL</label>
                <input
                  type="text"
                  value={formData.instaRequiredUrl || "-"}
                  readOnly
                />
              </div>
            </div>
            <div className="info-row">
              <div className="form-field">
                <label>신청 수량(총수량)</label>
                <input
                  type="text"
                  value={`${formData.totalQuantity || 0}회`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 시작일</label>
                <input
                  type="text"
                  value={formData.adStartDate || ""}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 종료일</label>
                <input type="text" value={formData.adEndDate || ""} readOnly />
              </div>
            </div>
          </div>
        </div>

        <div className="pricing-section">
          <div className="pricing-grid">
            <div className="form-field">
              <label>1회 단가</label>
              <input
                type="text"
                value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>
            <div className="form-field">
              <label>신청 수량</label>
              <input
                type="text"
                value={`${formData.totalQuantity}회`}
                readOnly
              />
            </div>
            <div className="form-field">
              <label>결제 금액</label>
              <input
                type="text"
                value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 완료 화면
  if (currentStep === 3) {
    return (
      <div className="advertisement-complete">
        <div className="complete-content">
          <div className="complete-icon">✅</div>
          <h2 className="complete-title">광고 등록이 완료되었습니다</h2>
          <p className="complete-description">
            등록하신 광고는 관리자 검토 후 승인됩니다.
            <br />
            승인 상태는 내 광고 확인에서 확인하실 수 있습니다.
          </p>
          <div className="complete-actions">
            <button
              className="complete-btn primary"
              onClick={() => onNext("myAdvertisement")}
            >
              내 광고 확인
            </button>
            <button
              className="complete-btn secondary"
              onClick={() => setCurrentStep(1)}
            >
              새 광고 등록
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-review-page">
      <div className="add-form-fields">
        <section className="add-form-block add-form-block-basic">
          <h3 className="add-form-block-title">기본 정보</h3>
          <div className="add-form-row">
            <div className="add-form-field">
              <label>홍보 대상 (상호·브랜드명)</label>
              <input
                type="text"
                placeholder="예: 앱이름"
                value={formData.storeName || ""}
                onChange={(e) => onInputChange("storeName", e.target.value)}
                disabled={!user?.id}
              />
            </div>
          </div>

          <div className="add-form-row">
            <div className="add-form-field">
              <label>필수 단어 (쉼표로 구분)</label>
              <input
                type="text"
                placeholder="예: 단어, #협찬"
                value={formData.approveAnswer || ""}
                onChange={(e) => onInputChange("approveAnswer", e.target.value)}
                disabled={!user?.id}
              />
              <p className="add-form-hint">
                참여자가 게시글에 반드시 넣어야 하는 단어입니다. 빠지면 자동으로
                거절됩니다.
                {requiredWords.length > 0 && !hasDisclosureTag && (
                  <>
                    <br />
                    대가를 받은 게시물은 #광고 또는 #협찬 같은 표시를 넣는 것을
                    권장합니다.
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="add-form-row">
            <div className="add-form-field">
              <label>게시글에 포함할 URL (선택)</label>
              <input
                type="text"
                placeholder="예: https://goldttak.com/app"
                value={formData.instaRequiredUrl || ""}
                onChange={(e) =>
                  onInputChange("instaRequiredUrl", e.target.value)
                }
                onBlur={(e) =>
                  onInputChange("instaRequiredUrl", e.target.value.trim())
                }
                disabled={!user?.id}
                className={
                  isValidRequiredUrl(formData.instaRequiredUrl) ? "" : "error"
                }
              />
              <p className="add-form-hint">
                입력하면 게시글에 이 링크가 없을 때 자동으로 거절됩니다. 인스타
                게시글의 링크는 눌러지지 않고 글자로만 보입니다.
              </p>
            </div>
          </div>

          <div className="add-form-row">
            <div className="add-form-field">
              <label>게시 가이드 (선택)</label>
              <textarea
                rows={3}
                placeholder="예: 앱 화면 캡처와 함께 사용 후기를 한 줄 이상 적어주세요."
                value={formData.instaGuideText || ""}
                onChange={(e) =>
                  onInputChange("instaGuideText", e.target.value)
                }
                disabled={!user?.id}
              />
            </div>
          </div>
        </section>

        <section className="add-form-block add-form-block-schedule">
          <h3 className="add-form-block-title">수량 · 일정</h3>
          <div className="add-form-row daily-quantity-row">
            <div className="add-form-field">
              <label>신청 수량(총 수량)</label>
              <input
                type="text"
                placeholder="참여 인원 수"
                value={formData.totalQuantity}
                onChange={(e) =>
                  onInputChange(
                    "totalQuantity",
                    e.target.value.replace(/[^0-9]/g, ""),
                  )
                }
                disabled={!user?.id}
              />
            </div>
          </div>

          <div className="add-form-row">
            <div className="add-form-field">
              <label>광고 시작일</label>
              <WeekdayOnlyDatePicker
                value={formData.adStartDate}
                onChange={handleAdStartDateChange}
                minDate={effectiveMinDate}
                disabled={!user?.id}
                userId={user?.id}
                blockSaturday={!!zoomSettings?.end_submit_sat_off}
                blockSunday={!!zoomSettings?.end_submit_sun_off}
              />
            </div>
            <div className="add-form-field">
              <label>진행 일수</label>
              <input
                type="text"
                placeholder="일수 입력"
                value={formData.adDuration || ""}
                onChange={handleAdDurationChange}
                disabled={!user?.id || !formData.adStartDate}
              />
            </div>
            <div className="add-form-field">
              <label>광고 종료일</label>
              <WeekdayOnlyDatePicker
                value={formData.adEndDate}
                onChange={handleAdEndDateChange}
                minDate={formData.adStartDate || effectiveMinDate}
                disabled={!user?.id || !formData.adStartDate}
                weekdaysOnly={false}
                userId={user?.id}
              />
            </div>
          </div>
        </section>

        <section className="add-form-block add-form-block-summary">
          <h3 className="add-form-block-title">결제 요약</h3>
          <div className="add-form-row">
            <div className="add-form-field">
              <label>1회 단가</label>
              <input
                type="text"
                value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>
            <div className="add-form-field">
              <label>신청 수량</label>
              <input
                type="text"
                value={`${formData.totalQuantity}회`}
                readOnly
              />
            </div>
            <div className="add-form-field">
              <label>결제 금액</label>
              <input
                type="text"
                value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InstaPromotionPage;
